/**
 * @file Speedbump Dialog.
 */

/* global ally */

import { gsap } from 'gsap';

/**
 * Speedbump Dialog component.
 */
const vSpeedbumpDialog = () => {
	const bg        = document.querySelector('.full-body');
	const dialogs   = {};
	const dialogEls = document.querySelectorAll('.v-speedbump-dialog');
	class Dialog {
		constructor(el) {
			this.dialog    = el;
			this.overlay   = this.dialog.nextElementSibling;
			this.cancelBtn = this.dialog.querySelector('.v-speedbump-dialog__btn--cancel');
			this.okBtn     = this.dialog.querySelector('.v-speedbump-dialog__btn--ok');
			this.hidden    = this.dialog.classList.contains('v-speedbump-dialog__modal--hidden');

			this.focusTrap  = null;
			this.lastFocus  = null;
			this.clonedLink = null;
			this.escapeKey  = null;
			this.seen_id    = null;
			this.lastScroll = null;

			this.exit = gsap.to(this.dialog, {
				opacity:    0,
				duration:   0.1,
				paused:     true,
				onComplete: () => {
					this.dialog.classList.add('v-speedbump-dialog__modal--hidden');
					bg.setAttribute('aria-hidden', 'false');
					this.okBtn.removeEventListener('click', this.okBtnHandler.bind(this));

					if (this.focusTrap) {
						this.focusTrap.disengage();
					}

					// give focus back to last focused element.
					this.lastFocus.focus();

					if (this.clonedLink && this.clonedLink.parentNode) {
						this.clonedLink.parentNode.removeChild(this.clonedLink);
					}

					this.hidden = true;
					this.escapeKey.disengage();
				},
			});
		}

		/**
		 * Initialize link.
		 *
		 * @param {Element} l - Link element.
		 * @returns {Element} - Cloned link element.
		 */
		static initLink(l) {
			const newLink = l.cloneNode();

			if (!newLink.target) {
				newLink.target = '_blank';
			}

			if (!newLink.rel) {
				newLink.rel = 'noopener';
			}

			gsap.set(newLink, {
				visibility: 'hidden',
				position:   'absolute',
				width:      0,
				height:     0,
				overflow:   'hidden',
				opacity:    0,
			});

			l.parentNode.appendChild(newLink);

			return newLink;
		}

		showDialog(target) {
			if (!this.hidden) {
				return;
			}

			this.clonedLink = Dialog.initLink(target.closest('[class*="v-speedbump"]'));
			this.lastFocus  = target.closest('[class*="v-speedbump"]') || document.activeElement;

			this.okBtn.addEventListener('click', (e) => this.okBtnHandler(e));

			this.escapeKey = ally.when.key({
				escape: () => {
					this.exit.restart();
				},
			});

			if (this.seen_id) {
				if (localStorage.getItem(this.seen_id)) {
					this.okBtn.click();
					return;
				}

				localStorage.setItem(this.seen_id, 'seen');
			}

			gsap.to(this.dialog, {
				opacity:  1,
				duration: 0.2,
				onStart:  () => {
					this.dialog.classList.remove('v-speedbump-dialog__modal--hidden');
					this.hidden     = false;
					this.lastScroll = window.pageYOffset;

					// Focus trapping
					this.focusTrap = ally.maintain.tabFocus({
						context: this.dialog,
					});

					this.dialog.focus();
				},
			});

			bg.setAttribute('aria-hidden', 'true');
		}

		hideDialog(e) {
			e.preventDefault();
			this.exit.restart();
		}

		okBtnHandler(e) {
			e.preventDefault();

			this.clonedLink.click();

			this.exit.restart();
		}

		lockScroll() {
			if (!this.hidden) {
				window.scrollTo(0, this.lastScroll);
			}
		}

		init() {
			if (this.dialog.dataset.seen) {
				this.seen_id = this.dialog.dataset.seen;

				if (localStorage.getItem(this.seen_id)) {
					return;
				}
			}

			if (this.cancelBtn) {
				this.cancelBtn.addEventListener('click', this.hideDialog.bind(this));
			}

			if (this.overlay) {
				this.overlay.addEventListener('click', this.hideDialog.bind(this));
			}
		}
	}

	[...dialogEls].forEach((d) => {
		dialogs[d.getAttribute('id')] = new Dialog(d);
		dialogs[d.getAttribute('id')].init();
	});

	const externalLinks  = document.querySelectorAll('a.v-speedbump');
	const customTriggers = document.querySelectorAll('.v-speedbump-custom');

	[...externalLinks].forEach((eL) => {
		eL.addEventListener('click', (e) => {
			e.preventDefault();

			const { target } = e;
			dialogs['v-external-speedbump'].showDialog(target);
		});
	});

	[...customTriggers].forEach((trigger) => {
		const matchingDialog = trigger.dataset.dialog;
		trigger.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopImmediatePropagation();
			const { target } = e;
			dialogs[matchingDialog].showDialog(target);
		});
	});
};

export default vSpeedbumpDialog;
